import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import { useBlogCache } from './BlogCacheContext'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { BlogCard } from './CarouselStyles'
import { withTranslator } from '../HOCComponents/withTranslator'
import themeColors from '@layers-frontend/commons/styles/themeColors'
const CARD_WIDTH = 220 // px
const CARD_HEIGHT = 150 // px

const BLOG_API_URLS = {
  es: '/blog/wp-json/wp/v2/posts?per_page=12&_embed',
  en: '/blog/en/wp-json/wp/v2/posts?per_page=12&_embed',
  fr: '/blog/fr/wp-json/wp/v2/posts?per_page=12&_embed',
  pt: '/blog/pt-br/wp-json/wp/v2/posts?per_page=12&_embed'
}

function decodeHtml(html) {
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

function getFeaturedImage(post) {
  if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0] && post._embedded['wp:featuredmedia'][0].source_url) {
    return post._embedded['wp:featuredmedia'][0].source_url
  }
  return '/blog/wp-content/uploads/2023/12/layers-crop-master-blog-default.jpg'
}

const BlogCarouselv2 = ({ t }) => {
  const locale = useSelector(state => state.locale)
  const postsUrl = BLOG_API_URLS[locale] || BLOG_API_URLS.en

  const { blogCache, setBlogCache } = useBlogCache()
  const [loading, setLoading] = useState(false)
  const swiperRef = useRef(null)

  useEffect(() => {
    if (blogCache.posts && blogCache.posts[locale]) return
    const fetchBlogData = async () => {
      setLoading(true)
      try {
        const postsResponse = await fetch(postsUrl)

        if (!postsResponse.ok) throw new Error('error-fetching-blog-posts')

        const postsData = await postsResponse.json()

        setBlogCache(prev => ({
          ...prev,
          posts: { ...((prev && prev.posts) || {}), [locale]: postsData }
        }))
      } catch (error) {
        // A failed fetch is for the console only: the user gets the empty state below, not the error.
        console.error(error)
      }
      setLoading(false)
    }

    fetchBlogData()
  }, [postsUrl, locale, blogCache.posts, setBlogCache])

  const posts = blogCache.posts ? blogCache.posts[locale] : []

  if (loading) return <div style={{ textAlign: 'center', padding: 24 }}>{t ? t('Cargando...') : 'Cargando...'}</div>
  if (!posts || !posts.length)
    return <div style={{ textAlign: 'center', padding: 24 }}>{t ? t('No hay artículos disponibles') : 'No hay artículos disponibles'}</div>

  return (
    <>
      <style>{`
        .swiper-pagination-bullet {
          background: ${themeColors.lightGrey} !important;
          opacity: 1 !important;
        }
        .swiper-pagination-bullet-active {
          background: ${themeColors.mintColor} !important;
        }
        .swiper-button-prev, .swiper-button-next {
          color: ${themeColors.mintColor} !important;
          top: 54px !important;
        }
      `}</style>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        spaceBetween={30}
        slidesPerView="auto"
        loop={true}
        style={{ padding: '20px 0', width: '100%' }}
        key={posts.length}
        onSwiper={swiper => {
          swiperRef.current = swiper
        }}
      >
        {posts.map(post => (
          <SwiperSlide
            key={post.id}
            style={{
              width: CARD_WIDTH,
              maxWidth: CARD_WIDTH,
              minWidth: CARD_WIDTH,
              height: CARD_HEIGHT,
              maxHeight: CARD_HEIGHT,
              minHeight: CARD_HEIGHT,
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center'
            }}
          >
            <BlogCard
              component="a"
              href={post.link}
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={() => swiperRef.current && swiperRef.current.autoplay && swiperRef.current.autoplay.stop()}
              onMouseLeave={() => swiperRef.current && swiperRef.current.autoplay && swiperRef.current.autoplay.start()}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                width: '100%',
                height: '100%',
                textDecoration: 'none',
                margin: '-20px auto',
                borderRadius: 12,
                transition:
                  'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                <img
                  src={getFeaturedImage(post)}
                  alt={post.title && post.title.rendered ? decodeHtml(post.title.rendered) : 'Imagen del artículo'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  loading="lazy"
                />
                {/* Overlay degradado en la parte inferior */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: '55%',
                    background: 'linear-gradient(to top, rgba(8,44,63,0.7) 50%, rgba(8, 44, 63, 0.0) 100%)',
                    zIndex: 2
                  }}
                />
                {/* Texto sobre el overlay */}
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    zIndex: 3,
                    padding: '16px 16px 8px 16px',
                    color: '#fff',
                    textShadow: '0 2px 8px rgba(0,0,0,0.5)'
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {post.title && post.title.rendered ? decodeHtml(post.title.rendered) : t ? t('Sin título') : 'Sin título'}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {post.date
                      ? new Date(post.date).toLocaleDateString(locale, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : ''}
                  </div>
                </div>
              </div>
            </BlogCard>
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  )
}

BlogCarouselv2.propTypes = {
  t: PropTypes.func
}

export default withTranslator(BlogCarouselv2)
